import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock } from "lucide-react";

const TIME_SLOTS = ["10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

function nextBusinessDays(count = 10) {
  const days = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    // Skip Sundays (0)
    if (d.getDay() !== 0) days.push(new Date(d));
  }
  return days;
}

const DAY_NAMES = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function BookingWidget({ propertySlug, propertyTitle, locality }) {
  const days = nextBusinessDays(7);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDay || !selectedTime) {
      toast.error("Escolha um dia e uma hora.");
      return;
    }
    setSubmitting(true);
    try {
      const dateStr = selectedDay.toISOString().slice(0, 10);
      await api.post("/leads", {
        ...form,
        lead_type: "visita",
        zone: locality || "",
        property_slug: propertySlug,
        message: `Pedido de visita a ${propertyTitle}\nData: ${dateStr} às ${selectedTime}`,
      });
      navigate("/obrigado?type=contacto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Não foi possível enviar.");
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#103C4A]/30";

  return (
    <div data-testid="booking-widget" className="card-soft p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
        <CalendarDays className="h-3.5 w-3.5" /> Marcar visita
      </div>
      <h3 className="mt-2 font-display text-xl text-stone-900">Escolha um horário</h3>
      <p className="mt-1 text-xs text-stone-500">Selecione a data e a hora. A Zélia confirma no próprio dia útil.</p>

      <form onSubmit={onSubmit} className="mt-5 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Dia</div>
          <div className="grid grid-cols-4 gap-2" data-testid="booking-days">
            {days.map((d) => {
              const iso = d.toISOString().slice(0, 10);
              const active = selectedDay && selectedDay.toISOString().slice(0, 10) === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => setSelectedDay(d)}
                  data-testid={`booking-day-${iso}`}
                  className={`rounded-lg border px-2 py-2 text-center transition-colors ${
                    active ? "border-[#103C4A] bg-[#103C4A] text-white" : "border-stone-200 bg-white text-stone-800 hover:border-[#103C4A]"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-widest opacity-80">{DAY_NAMES[d.getDay()]}</div>
                  <div className="font-display text-lg leading-none mt-0.5">{d.getDate()}</div>
                  <div className="text-[10px] opacity-80 mt-0.5">{d.toLocaleString("pt-PT", { month: "short" })}</div>
                </button>
              );
            })}
          </div>
        </div>

        {selectedDay && (
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Hora</div>
            <div className="grid grid-cols-4 gap-2" data-testid="booking-times">
              {TIME_SLOTS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSelectedTime(t)}
                  data-testid={`booking-time-${t}`}
                  className={`rounded-lg border px-2 py-2 text-sm transition-colors ${
                    selectedTime === t ? "border-[#103C4A] bg-[#103C4A] text-white" : "border-stone-200 bg-white text-stone-800 hover:border-[#103C4A]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedTime && (
          <div className="space-y-3 pt-2 border-t border-stone-100">
            <input required placeholder="Nome" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="booking-name" className={inputCls} />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="booking-email" className={inputCls} />
            <input placeholder="Telefone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="booking-phone" className={inputCls} />
            <button type="submit" disabled={submitting} data-testid="booking-submit" className="btn-primary w-full">
              {submitting ? "A enviar..." : "Pedir esta visita"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
